/*!
 * Myvicto Quiz — Find Your Putting Match
 * Custom Shopify quiz engine. No third-party dependencies.
 *
 * Architecture:
 *   - IIFE pattern (single global window.MyvictoQuiz)
 *   - State machine with localStorage persistence (7-day TTL)
 *   - Klaviyo Public API client (browser-side, with retry queue)
 *   - Pure scoring function (deterministic, testable)
 *   - BEM CSS classes (myvicto-quiz__*)
 *
 * Sprint 1A scope: email gate + Q1 + Klaviyo "Quiz Started" event
 * Sprint 2 will add: Q2-Q8, scoring, reco cards
 */
(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════
  // CONSTANTS
  // ═══════════════════════════════════════════════════════════════════════
  const VERSION = 'v1.0.0-sprint2';
  console.log('%c[MyvictoQuiz] Sprint 2 engine loaded — v1.0.0-sprint2', 'color:#C0A868;font-weight:bold');
  const STORAGE_KEY = 'myvicto_quiz_state';
  const RETRY_QUEUE_KEY = 'myvicto_quiz_retry_queue';
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_BASE = 'https://a.klaviyo.com/client';
  const RETRY_BACKOFF_MS = [1000, 4000, 16000, 64000];

  const COUNTRY_TO_LANG = {
    US: 'en', UK: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en', OTHER: 'en',
    FR: 'fr', BE: 'fr', CH: 'fr', LU: 'fr', MC: 'fr',
    DE: 'de', AT: 'de',
  };

  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  let state = null;
  let config = null;
  let root = null;

  function freshState() {
    return {
      version: VERSION,
      sessionId: uuid(),
      startedAt: new Date().toISOString(),
      profile: { email: null, firstName: null, country: null, language: null, marketingConsent: true },
      track: null,
      answers: {},
      currentScreenId: 'email_gate',
      history: [],
      completed: false,
      lastSavedAt: null,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'sess-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);
  }

  function $(sel, parent) { return (parent || root).querySelector(sel); }
  function $$(sel, parent) { return Array.from((parent || root).querySelectorAll(sel)); }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (v == null) continue;
        if (k === 'class') node.className = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
        else if (k === 'data') for (const [dk, dv] of Object.entries(v)) node.dataset[dk] = dv;
        else node.setAttribute(k, v);
      }
    }
    if (children) {
      for (const c of [].concat(children)) {
        if (c == null) continue;
        if (typeof c === 'string') node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      }
    }
    return node;
  }

  /* Splits text on \n and returns an array of text nodes + <br> elements,
     so authors can force line breaks via \n inside config.json strings.
     Used for hero title/subtitle and question headings to balance lines. */
  function multiline(text) {
    if (!text) return [];
    const out = [];
    String(text).split('\n').forEach((line, i) => {
      if (i > 0) out.push(document.createElement('br'));
      out.push(document.createTextNode(line));
    });
    return out;
  }

  function isValidEmail(email) {
    return typeof email === 'string' &&
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) &&
      email.length <= 254;
  }

  function log(level, msg, data) {
    const prefix = '[MyvictoQuiz]';
    if (level === 'error') console.error(prefix, msg, data || '');
    else if (level === 'warn') console.warn(prefix, msg, data || '');
    else console.log(prefix, msg, data || '');
  }

  // ═══════════════════════════════════════════════════════════════════════
  // STATE PERSISTENCE
  // ═══════════════════════════════════════════════════════════════════════
  function saveState() {
    if (!state) return;
    state.lastSavedAt = new Date().toISOString();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      log('warn', 'localStorage unavailable, state will not persist', e.message);
    }
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.lastSavedAt) return null;
      const age = Date.now() - new Date(parsed.lastSavedAt).getTime();
      if (age > STORAGE_TTL_MS) {
        log('info', 'Saved state expired (>7 days), starting fresh');
        clearState();
        return null;
      }
      if (parsed.completed) {
        // Don't auto-resume completed quizzes; let user start fresh
        return null;
      }
      return parsed;
    } catch (e) {
      log('warn', 'Failed to load state', e.message);
      return null;
    }
  }

  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // KLAVIYO PUBLIC API CLIENT
  // ═══════════════════════════════════════════════════════════════════════
  async function klaviyoFetch(endpoint, body) {
    const companyId = config.settings.klaviyo_company_id;
    const url = `${KLAVIYO_BASE}/${endpoint}/?company_id=${companyId}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (res.status === 202 || res.status === 200) return { ok: true, status: res.status };
    const text = await res.text().catch(() => '');
    return { ok: false, status: res.status, body: text };
  }

  async function klaviyoSubscribe({ email, firstName, marketingConsent }) {
    const listId = config.settings.klaviyo_email_list_id;
    const attributes = { email };
    if (firstName) attributes.first_name = firstName;
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Myvicto Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes,
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };
    return klaviyoFetch('subscriptions', body);
  }

  async function klaviyoUpdateProfile({ email, firstName, customProperties }) {
    /* Write standard attributes + custom properties to the profile.
       Uses POST /client/profiles/ which upserts by email. */
    const attributes = { email, properties: customProperties || {} };
    if (firstName) attributes.first_name = firstName;
    const body = {
      data: { type: 'profile', attributes },
    };
    return klaviyoFetch('profiles', body);
  }

  async function klaviyoTrack(eventName, profile, properties, uniqueId) {
    const body = {
      data: {
        type: 'event',
        attributes: {
          unique_id: uniqueId,
          metric: { data: { type: 'metric', attributes: { name: eventName } } },
          properties: properties || {},
          profile: { data: { type: 'profile', attributes: profile } },
          time: new Date().toISOString(),
        },
      },
    };
    return klaviyoFetch('events', body);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RETRY QUEUE (for failed Klaviyo calls)
  // ═══════════════════════════════════════════════════════════════════════
  function enqueueRetry(payload) {
    try {
      const queue = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
      queue.push({ ...payload, queuedAt: Date.now(), attempts: 0 });
      localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(queue));
    } catch (e) { log('warn', 'Could not enqueue retry', e.message); }
  }

  async function drainRetryQueue() {
    let queue;
    try {
      queue = JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
    } catch (e) { return; }
    if (queue.length === 0) return;
    log('info', `Draining ${queue.length} retry queue items`);
    const remaining = [];
    for (const item of queue) {
      const result = await retryItem(item);
      if (!result.ok) remaining.push({ ...item, attempts: item.attempts + 1 });
    }
    try { localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(remaining)); } catch (e) {}
  }

  async function retryItem(item) {
    if (item.endpoint === 'subscriptions') return klaviyoFetch('subscriptions', item.body);
    if (item.endpoint === 'events') return klaviyoFetch('events', item.body);
    return { ok: false };
  }

  async function withRetry(fn, payloadForQueue) {
    for (let i = 0; i < RETRY_BACKOFF_MS.length; i++) {
      try {
        const result = await fn();
        if (result.ok) return result;
        if (result.status === 400 || result.status === 401 || result.status === 403) {
          log('error', 'Klaviyo permanent error, not retrying', result);
          return result;
        }
      } catch (e) {
        log('warn', `Klaviyo attempt ${i + 1} failed`, e.message);
      }
      await new Promise(r => setTimeout(r, RETRY_BACKOFF_MS[i] + Math.random() * 500));
    }
    if (payloadForQueue) enqueueRetry(payloadForQueue);
    return { ok: false, status: 0, body: 'all_retries_failed_queued' };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SCREEN RENDERERS
  // ═══════════════════════════════════════════════════════════════════════
  function clearScreen() {
    while (root.firstChild) root.removeChild(root.firstChild);
  }

  function renderProgressBar() {
    if (!config.settings.progress_bar) return null;
    const totalQs = config.questions.length;
    const answered = Object.keys(state.answers).length;
    const pct = Math.min(100, Math.round((answered / totalQs) * 100));
    return el('div', { class: 'myvicto-quiz__progress' }, [
      el('div', { class: 'myvicto-quiz__progress-bar', style: `width:${pct}%` })
    ]);
  }

  // ─── Email Gate ─────────────────────────────────────────────────────────
  function renderEmailGate() {
    clearScreen();
    state.currentScreenId = 'email_gate';
    saveState();

    const screen = config.screens.find(s => s.id === 'email_gate');
    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--email-gate' });

    container.appendChild(el('div', { class: 'myvicto-quiz__eyebrow' }, 'Putter fitting · 60 seconds'));
    container.appendChild(el('h1', { class: 'myvicto-quiz__title myvicto-quiz__title--hero' }, multiline(screen.title)));
    container.appendChild(el('p', { class: 'myvicto-quiz__subtitle' }, multiline(screen.subtitle)));

    const form = el('form', { class: 'myvicto-quiz__form', novalidate: 'novalidate' });

    // Email field
    const emailWrap = el('div', { class: 'myvicto-quiz__field' });
    emailWrap.appendChild(el('label', { class: 'myvicto-quiz__label', for: 'mvq-email' }, 'Email'));
    const emailInput = el('input', { type: 'email', id: 'mvq-email', name: 'email', class: 'myvicto-quiz__input', required: 'required', autocomplete: 'email', placeholder: 'you@example.com' });
    emailWrap.appendChild(emailInput);
    const emailError = el('div', { class: 'myvicto-quiz__field-error', 'aria-live': 'polite' });
    emailWrap.appendChild(emailError);
    form.appendChild(emailWrap);

    // First name field
    const fnWrap = el('div', { class: 'myvicto-quiz__field' });
    fnWrap.appendChild(el('label', { class: 'myvicto-quiz__label', for: 'mvq-fname' }, 'First name (optional)'));
    const fnInput = el('input', { type: 'text', id: 'mvq-fname', name: 'first_name', class: 'myvicto-quiz__input', autocomplete: 'given-name' });
    fnWrap.appendChild(fnInput);
    form.appendChild(fnWrap);

    // Country field — now optional, defaults to US
    const ctWrap = el('div', { class: 'myvicto-quiz__field' });
    ctWrap.appendChild(el('label', { class: 'myvicto-quiz__label', for: 'mvq-country' }, 'Country (optional)'));
    const ctSelect = el('select', { id: 'mvq-country', name: 'country', class: 'myvicto-quiz__select' });
    const countryField = screen.fields.find(f => f.key === 'country');
    for (const opt of countryField.options) {
      const optEl = el('option', { value: opt.value }, opt.label);
      if (opt.value === 'US') optEl.selected = true; // default US
      ctSelect.appendChild(optEl);
    }
    ctWrap.appendChild(ctSelect);
    form.appendChild(ctWrap);

    // Marketing consent
    const csWrap = el('div', { class: 'myvicto-quiz__consent' });
    const csInput = el('input', { type: 'checkbox', id: 'mvq-consent', class: 'myvicto-quiz__checkbox' });
    if (screen.marketing_consent_default) csInput.checked = true;
    csWrap.appendChild(csInput);
    csWrap.appendChild(el('label', { for: 'mvq-consent', class: 'myvicto-quiz__consent-label' }, screen.marketing_consent_label));
    form.appendChild(csWrap);

    // Submit button
    const submitBtn = el('button', { type: 'submit', class: 'myvicto-quiz__cta' }, screen.cta);
    form.appendChild(submitBtn);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      emailError.textContent = '';

      const email = emailInput.value.trim();
      const firstName = fnInput.value.trim() || null;
      const country = ctSelect.value || 'US';
      const consent = csInput.checked;

      if (!isValidEmail(email)) {
        emailError.textContent = 'Please enter a valid email address.';
        emailInput.focus();
        return;
      }

      // Update state and persist immediately
      state.profile.email = email;
      state.profile.firstName = firstName;
      state.profile.country = country;
      state.profile.language = COUNTRY_TO_LANG[country] || 'en';
      state.profile.marketingConsent = consent;
      saveState();

      // Klaviyo ONSITE identify — pose le cookie first-party __kla_id pour lier la
      // navigation post-quiz (Viewed Product / Active on Site) a ce profil, ce qui
      // alimente le flow Browse Abandonment. Gate sur consent (EU/ePrivacy). company_id YebhwC.
      if (consent) {
        try {
          window.klaviyo = window.klaviyo || [];
          window.klaviyo.identify({ email: email, first_name: firstName });
        } catch (e) { /* onsite SDK absent: no-op */ }
      }

      // Fire Klaviyo IN BACKGROUND (fire-and-forget). UI advances immediately.
      // keepalive: true ensures requests complete even if user navigates away.
      const customProps = {
        country,
        language: state.profile.language,
        quiz_marketing_consent: !!consent,
        quiz_session_id: state.sessionId,
        quiz_started_at: state.startedAt,
      };
      Promise.allSettled([
        withRetry(() => klaviyoUpdateProfile({ email, firstName, customProperties: customProps }), null),
        withRetry(() => klaviyoSubscribe({ email, firstName, marketingConsent: consent }), null),
        withRetry(() => klaviyoTrack(
          'Quiz Started',
          { email },
          {
            quiz_session_id: state.sessionId,
            quiz_started_at: state.startedAt,
            country,
            language: state.profile.language,
          },
          state.sessionId + '-started'
        ), null),
      ]).then(results => {
        const ok = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
        log('info', `Klaviyo background: ${ok}/3 calls ok`);
      });

      // Meta Pixel — quiz STARTED (email captured). Mid-funnel retargeting signal. Additive/defensive.
      if (typeof window.fbq === 'function') {
        try {
          window.fbq('trackCustom', 'QuizStarted', { content_name: 'Myvicto Quiz Started', content_category: 'putter_fitting' }, { eventID: state.sessionId + '-started' });
        } catch (e) {}
      }

      // V9: Advance UI IMMEDIATELY to first question (no track choice in V9)
      state.track = 'all';
      renderQuestion(0);
    });

    container.appendChild(form);
    root.appendChild(container);
    emailInput.focus();
  }

  // ─── Track Choice ───────────────────────────────────────────────────────
  function renderTrackChoice() {
    clearScreen();
    state.currentScreenId = 'track_choice';
    saveState();

    const screen = config.screens.find(s => s.id === 'track_choice');
    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--track-choice' });
    container.appendChild(el('h2', { class: 'myvicto-quiz__title' }, screen.title));

    const cards = el('div', { class: 'myvicto-quiz__track-cards' });
    for (const opt of screen.options) {
      const card = el('button', {
        type: 'button',
        class: 'myvicto-quiz__track-card',
        data: { value: opt.value },
        onclick: () => {
          state.track = opt.value;
          state.history.push('track_choice');
          saveState();
          renderQuestion(0);
        },
      }, [
        el('div', { class: 'myvicto-quiz__track-meta' }, [
          el('span', { class: 'myvicto-quiz__track-time' }, opt.time_label),
          el('span', { class: 'myvicto-quiz__track-counter' }, opt.counter_label),
        ]),
        el('h3', { class: 'myvicto-quiz__track-title' }, opt.title),
        el('p', { class: 'myvicto-quiz__track-description' }, opt.description),
      ]);
      cards.appendChild(card);
    }
    container.appendChild(cards);
    root.appendChild(container);
  }

  // ─── Question Renderer ──────────────────────────────────────────────────
  function getActiveQuestions() {
    return config.questions
      .filter(q => q.track === 'all' || q.track === state.track)
      .sort((a, b) => a.order - b.order);
  }

  function renderQuestion(index) {
    const questions = getActiveQuestions();
    if (index >= questions.length) {
      complete();
      return;
    }
    const q = questions[index];
    state.currentScreenId = q.id;
    saveState();

    clearScreen();
    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--question' });
    const progress = renderProgressBar();
    if (progress) container.appendChild(progress);

    container.appendChild(el('h2', { class: 'myvicto-quiz__question' }, multiline(q.question)));
    if (q.tooltip) container.appendChild(el('p', { class: 'myvicto-quiz__tooltip' }, q.tooltip));

    /* Column count for media-grid = number of illustrated options, so 3-img and
       4-img questions both render in a single row. Text-only fallbacks render
       below the grid as a full-width card (white text, premium look). */
    const mediaCount = q.options.filter(o => !!o.image_url).length;
    const optsContainer = el('div', {
      class: q.type === 'media-grid' ? 'myvicto-quiz__options myvicto-quiz__options--grid' : 'myvicto-quiz__options',
      style: q.type === 'media-grid' && mediaCount > 0 ? '--mvq-media-cols: ' + mediaCount : null,
    });
    let secondaryContainer = null;

    for (const opt of q.options) {
      const hasImage = !!opt.image_url;
      // In media-grid, text-only options render below the grid as a fallback card (not in the row)
      const isSecondaryPill = q.type === 'media-grid' && !hasImage;
      let btnClass;
      if (isSecondaryPill) {
        btnClass = 'myvicto-quiz__option-secondary';
      } else {
        btnClass = 'myvicto-quiz__option' + (hasImage ? ' myvicto-quiz__option--media' : '');
      }
      const optBtn = el('button', {
        type: 'button',
        class: btnClass,
        data: { value: opt.value },
        onclick: () => {
          state.answers[q.id] = opt.value;
          state.history.push(q.id);
          saveState();
          if (q.auto_advance) {
            const selectedClass = isSecondaryPill ? 'myvicto-quiz__option-secondary--selected' : 'myvicto-quiz__option--selected';
            optBtn.classList.add(selectedClass);
            setTimeout(() => renderQuestion(index + 1), config.settings.auto_advance_delay_ms || 350);
          }
        },
      });
      if (hasImage) {
        const imgWrap = el('div', { class: 'myvicto-quiz__option-image' });
        imgWrap.appendChild(el('img', { src: opt.image_url, alt: opt.label, loading: 'lazy' }));
        optBtn.appendChild(imgWrap);
        optBtn.appendChild(el('span', { class: 'myvicto-quiz__option-label' }, opt.label));
      } else {
        optBtn.textContent = opt.label;
      }
      if (isSecondaryPill) {
        if (!secondaryContainer) secondaryContainer = el('div', { class: 'myvicto-quiz__options-secondary' });
        secondaryContainer.appendChild(optBtn);
      } else {
        optsContainer.appendChild(optBtn);
      }
    }
    container.appendChild(optsContainer);
    if (secondaryContainer) container.appendChild(secondaryContainer);

    // Back button
    if (config.settings.allow_back && state.history.length > 0) {
      const backBtn = el('button', {
        type: 'button',
        class: 'myvicto-quiz__back',
        onclick: () => {
          if (index > 0) {
            const prevQ = questions[index - 1];
            delete state.answers[prevQ.id];
            state.history.pop();
            renderQuestion(index - 1);
          } else {
            // V9: no track choice — back from Q1 returns to email gate
            renderEmailGate();
          }
        },
      }, '← Back');
      container.appendChild(backBtn);
    }

    root.appendChild(container);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SCORING ENGINE (deterministic pure functions)
  // ═══════════════════════════════════════════════════════════════════════
  function getInlineLabel(value) {
    if (!value) return '';
    return String(value).replace(/-/g, ' ').toLowerCase();
  }

  function getOptionLabel(questionId, value) {
    const q = config.questions.find(qq => qq.id === questionId);
    if (!q) return value;
    const opt = q.options.find(o => o.value === value);
    return opt ? opt.label : value;
  }

  function getProductBySerie(serie) {
    return config.products.find(p => p.serie === serie);
  }

  /* Evaluate one rule's `if` block against answers. Returns true if all clauses match. */
  function evaluateOverrideRule(rule, answers) {
    if (!rule.if) return false;
    if (rule.track && rule.track !== 'all' && rule.track !== state.track) return false;
    for (const [key, expected] of Object.entries(rule.if)) {
      let baseKey = key;
      let mode = 'eq';
      if (key.endsWith('_not_in')) { baseKey = key.slice(0, -7); mode = 'not_in'; }
      else if (key.endsWith('_in')) { baseKey = key.slice(0, -3); mode = 'in'; }
      else if (key.endsWith('_not')) { baseKey = key.slice(0, -4); mode = 'neq'; }
      const actual = answers[baseKey];
      if (mode === 'eq' && actual !== expected) return false;
      if (mode === 'neq' && actual === expected) return false;
      if (mode === 'in' && !Array.isArray(expected)) return false;
      if (mode === 'in' && !expected.includes(actual)) return false;
      if (mode === 'not_in' && Array.isArray(expected) && expected.includes(actual)) return false;
    }
    return true;
  }

  /* Sum-scoring across all answered questions. Returns { E, L, Z, M, C, B }. */
  function computeRawScores(answers) {
    const scores = { E: 0, L: 0, Z: 0, M: 0, C: 0, B: 0 };
    for (const q of config.questions) {
      const ans = answers[q.id];
      if (!ans) continue;
      const matrix = q.scoring && q.scoring[ans];
      if (!matrix) continue;
      for (const k of Object.keys(scores)) {
        if (typeof matrix[k] === 'number') scores[k] += matrix[k];
      }
    }
    return scores;
  }

  /* Apply tiebreak logic when top 2 series are within 2 points. */
  function applyTiebreak(scores, answers) {
    const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    if (ranked.length < 2) return ranked[0][0];
    const top = ranked[0][1];
    const second = ranked[1][1];
    if (top - second > 2) return ranked[0][0];

    /* V9: Q4 feature priority ×0.5, Q2 misses ×0.3 weighted boost */
    const adjusted = { ...scores };
    const q4 = answers.q4_feature_priority;
    const q4Matrix = config.questions.find(q => q.id === 'q4_feature_priority')?.scoring?.[q4];
    if (q4Matrix) for (const k of Object.keys(adjusted)) adjusted[k] += (q4Matrix[k] || 0) * 0.5;

    const q2 = answers.q2_misses_pattern;
    const q2Matrix = config.questions.find(q => q.id === 'q2_misses_pattern')?.scoring?.[q2];
    if (q2Matrix) for (const k of Object.keys(adjusted)) adjusted[k] += (q2Matrix[k] || 0) * 0.3;

    const re = Object.entries(adjusted).sort((a, b) => b[1] - a[1]);
    const newTop = re[0][1];
    const newSecond = re[1][1];
    if (newTop - newSecond > 0.5) return re[0][0];

    /* V9 Q3 shape hard tiebreak */
    const shapeMap = { mallet: 'E-Serie', 'mid-mallet': 'Z-Serie', blade: 'B-Serie' };
    const q3 = answers.q3_shape_preference;
    if (shapeMap[q3]) return shapeMap[q3].split('-')[0];

    /* V9 Q4 feature hard tiebreak */
    const featureMap = { alignment: 'L-Serie', feel: 'B-Serie', technology: 'M-Serie', forgiveness: 'E-Serie' };
    if (featureMap[q4]) return featureMap[q4].split('-')[0];

    /* Conflicting → fallback E-Serie (most universal fit) */
    return 'E';
  }

  /* Main scoring function. Returns full result object. */
  function score(state) {
    const answers = state.answers;
    const rawScores = computeRawScores(answers);
    const appliedOverrides = [];

    /* Step 1: override rules in priority order */
    const sortedRules = [...(config.override_rules || [])].sort((a, b) => a.priority - b.priority);
    for (const rule of sortedRules) {
      if (evaluateOverrideRule(rule, answers)) {
        appliedOverrides.push(rule.id);

        if (rule.action === 'fitting_route') {
          return {
            kind: 'fitting',
            fitting_route: rule.id,
            fitting_subject: rule.subject,
            fitting_message: rule.message,
            primary_reco: null,
            primary_url: null,
            raw_scores: rawScores,
            applied_overrides: appliedOverrides,
          };
        }

        if (rule.action === 'force_serie' || rule.action === 'force_serie_with_hosel') {
          const product = getProductBySerie(rule.serie);
          return {
            kind: 'single',
            primary_reco: rule.serie,
            primary_url: product ? product.url : null,
            primary_model: product ? product.model : null,
            forced_hosel: rule.hosel || null,
            trigger_reason: 'override:' + rule.id,
            raw_scores: rawScores,
            applied_overrides: appliedOverrides,
          };
        }

        if (rule.action === 'dual_reco') {
          const left = getProductBySerie(rule.left);
          const right = getProductBySerie(rule.right);
          const dualScreen = config.dual_reco_screens && config.dual_reco_screens[rule.trigger];
          return {
            kind: 'dual',
            dual_trigger: rule.trigger,
            dual_screen: dualScreen,
            dual_left: { serie: rule.left, product: left },
            dual_right: { serie: rule.right, product: right },
            primary_reco: null,
            raw_scores: rawScores,
            applied_overrides: appliedOverrides,
          };
        }
      }
    }

    /* Step 2: sum scoring + tiebreak fallback */
    const winner = applyTiebreak(rawScores, answers);
    const winnerSerieId = winner + '-Serie';
    const product = getProductBySerie(winnerSerieId);
    return {
      kind: 'single',
      primary_reco: winnerSerieId,
      primary_url: product ? product.url : null,
      primary_model: product ? product.model : null,
      trigger_reason: 'sum_scoring',
      raw_scores: rawScores,
      applied_overrides: appliedOverrides,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RECO RENDERERS
  // ═══════════════════════════════════════════════════════════════════════
  function substituteTokens(template, context) {
    return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) =>
      context[key] !== undefined && context[key] !== null ? String(context[key]) : ''
    );
  }

  function buildContext(serie, model) {
    return {
      first_name: state.profile.firstName || 'There',
      email: state.profile.email,
      model: model || '',
      serie: serie || '',
      handedness_label: '',
      stroke_arc_label: getInlineLabel(state.answers.q2_misses_pattern) || 'natural',
      frustration_label: getInlineLabel(state.answers.q4_feature_priority) || 'better putting',
    };
  }

  function paragraphsFromText(text) {
    return String(text).split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  }

  /* ────────────────────────────────────────────────────────────────────
     Engineer Dossier — series-themed reveal card (V7 design)
     Accent class scopes --accent via CSS (E/L/Z/M/C/B).
     ──────────────────────────────────────────────────────────────────── */
  const SERIES_ACCENT = { 'E-Serie':'E', 'L-Serie':'L', 'Z-Serie':'Z', 'M-Serie':'M', 'C-Serie':'C', 'B-Serie':'B' };
  const SERIES_TAGLINE = {
    'E-Serie': 'The most configurable myvicto.',
    'L-Serie': 'The alignment-led myvicto.',
    'Z-Serie': 'The myvicto mid-mallet.',
    'M-Serie': 'The first myvicto.',
    'C-Serie': 'The premium mid-mallet.',
    'B-Serie': 'The curved-face blade.'
  };
  const SERIES_SPECS = {
    'E-Serie': [
      ['Series', 'E-Serie'], ['Shafts', '4 positions, incl. Optimized Torque'],
      ['Shape', 'Mallet'], ['Lie angles', '9'],
      ['Sweet spot', '6mm vertical'], ['Finish', '8 colours']
    ],
    'L-Serie': [
      ['Series', 'L-Serie'], ['Lie angles', '9'],
      ['Shape', 'Mallet'], ['Sweet spot', '6mm vertical'],
      ['Shaft', 'One fixed position'], ['Finish', '3 colours']
    ],
    'Z-Serie': [
      ['Series', 'Z-Serie'], ['Stroke', 'All strokes'],
      ['Shape', 'Mid-mallet'], ['Sweet spot', '6mm vertical'],
      ['Shafts', '3 positions'], ['Finish', '8 colours']
    ],
    'M-Serie': [
      ['Series', 'M-Serie'], ['Weights', '10 / 20 / 30g'],
      ['Shape', 'Cubic mallet'], ['Shafts', '4 positions, incl. Optimized Torque'],
      ['Sweet spot', '6mm vertical'], ['Finish', '8 colours']
    ],
    'C-Serie': [
      ['Series', 'C-Serie'], ['Hosels', '3-hosel system'],
      ['Shape', 'Mid-mallet'], ['Stroke', 'All strokes'],
      ['Weight', 'Heaviest in lineup'], ['Finish', '4 colours']
    ],
    'B-Serie': [
      ['Series', 'B-Serie'], ['Hosels', '3 positions'],
      ['Shape', 'Blade'], ['Sweet spot', '6mm vertical'],
      ['Stroke', 'All strokes'], ['Finish', '4 colours']
    ]
  };

  function splitParagraphAtMidpoint(text) {
    const sentences = String(text).split(/(?<=\.)\s+/).filter(s => s.trim());
    if (sentences.length <= 2) return [String(text)];
    const mid = Math.ceil(sentences.length / 2);
    return [sentences.slice(0, mid).join(' '), sentences.slice(mid).join(' ')];
  }

  function renderRecoCard(result) {
    clearScreen();
    const tpl = config.result_card_template;
    const product = getProductBySerie(result.primary_reco);
    const seriesCopy = tpl.series_copy[result.primary_reco];
    if (!product || !seriesCopy) {
      log('error', 'Missing product or copy for serie', result.primary_reco);
      return renderFallback();
    }
    const context = buildContext(result.primary_reco, product.model);
    const accent = SERIES_ACCENT[result.primary_reco] || 'B';

    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--result' });
    const dossier = el('div', { class: 'myvicto-quiz__dossier myvicto-quiz__dossier--' + accent });

    /* Top bar */
    dossier.appendChild(el('div', { class: 'myvicto-quiz__dossier-topbar' }, 'Fitting analysis'));

    /* Grid: image left, copy right */
    const grid = el('div', { class: 'myvicto-quiz__dossier-grid' });

    const imageCol = el('div', { class: 'myvicto-quiz__dossier-image-col' });
    if (product.image_url) {
      imageCol.appendChild(el('img', {
        class: 'myvicto-quiz__dossier-image',
        src: product.image_url, alt: product.model, loading: 'lazy'
      }));
    }
    grid.appendChild(imageCol);

    const copyCol = el('div', { class: 'myvicto-quiz__dossier-copy-col' });
    copyCol.appendChild(el('div', { class: 'myvicto-quiz__dossier-eyebrow' }, tpl.top_label || 'Your match'));
    copyCol.appendChild(el('h1', { class: 'myvicto-quiz__dossier-title' }, product.model || result.primary_reco));
    const tagline = SERIES_TAGLINE[result.primary_reco] || (product.shape ? 'The myvicto ' + product.shape + '.' : '');
    if (tagline) {
      copyCol.appendChild(el('p', { class: 'myvicto-quiz__dossier-tagline' }, tagline));
    }
    copyCol.appendChild(el('div', { class: 'myvicto-quiz__dossier-rule' }));

    /* Body + specs: render ALL content paragraphs from config (V14 — multi-para
       readability), skip closer tagline ("curved by design...") and "your line".
       V15 (Gary 2026-05-20): wrap body + specs in a <details> disclosure so the
       mobile reveal fits one screen with the CTA above the fold. The disclosure
       renders OPEN on desktop (summary hidden — looks identical to before) and
       COLLAPSED on mobile (tap "Why this putter fits you" to expand). */
    const bodyText = substituteTokens(seriesCopy.body, context);
    const paragraphs = paragraphsFromText(bodyText)
      .filter(p => !p.toLowerCase().startsWith('curved by design') && !p.toLowerCase().startsWith('your line'));

    const disclosure = el('details', { class: 'myvicto-quiz__dossier-disclosure', open: 'open' });
    disclosure.appendChild(el('summary', { class: 'myvicto-quiz__dossier-disclosure-summary' }, 'Why this putter fits you'));
    const disclosureBody = el('div', { class: 'myvicto-quiz__dossier-disclosure-body' });

    const bodyWrap = el('div', { class: 'myvicto-quiz__dossier-body' });
    paragraphs.forEach(p => { if (p) bodyWrap.appendChild(el('p', {}, p)); });
    disclosureBody.appendChild(bodyWrap);

    const specs = SERIES_SPECS[result.primary_reco];
    if (specs && specs.length) {
      disclosureBody.appendChild(el('div', { class: 'myvicto-quiz__dossier-specs-title' }, 'Specs'));
      const specsGrid = el('div', { class: 'myvicto-quiz__dossier-specs' });
      specs.forEach(function (pair) {
        const line = el('div', { class: 'myvicto-quiz__dossier-spec-line' });
        line.appendChild(el('span', { class: 'key' }, pair[0]));
        line.appendChild(el('span', { class: 'val' }, pair[1]));
        specsGrid.appendChild(line);
      });
      disclosureBody.appendChild(specsGrid);
    }
    disclosure.appendChild(disclosureBody);
    copyCol.appendChild(disclosure);

    /* Collapse on mobile so the CTA + guarantee stay above the fold. */
    if (window.matchMedia && window.matchMedia('(max-width: 740px)').matches) {
      disclosure.removeAttribute('open');
    }
    grid.appendChild(copyCol);
    dossier.appendChild(grid);

    /* Bottom: CTA + guarantee (+ optional secondary) */
    const bottom = el('div', { class: 'myvicto-quiz__dossier-bottom' });
    bottom.appendChild(el('a', {
      href: product.url,
      class: 'myvicto-quiz__cta myvicto-quiz__cta--primary myvicto-quiz__dossier-cta'
    }, 'Configure your putter →'));
    bottom.appendChild(el('div', { class: 'myvicto-quiz__dossier-guarantee' },
      tpl.footer || 'Backed by our ' + (config.settings.guarantee_days || 30) + '-day Perfect Fit Guarantee.'));

    const uncertainAnswers = ['untracked', 'scatter', 'no-pattern', 'show-me'];
    const uncertainCount = Object.values(state.answers).filter(function (v) { return uncertainAnswers.indexOf(v) !== -1; }).length;
    if (uncertainCount >= 2) {
      const secWrap = el('div', { class: 'myvicto-quiz__dossier-secondary-wrap' });
      secWrap.appendChild(el('p', { class: 'myvicto-quiz__dossier-secondary-note' }, 'Want a deeper analysis?'));
      secWrap.appendChild(el('a', {
        href: 'mailto:' + (config.settings.fitting_email || 'fitting@myvicto.com') + '?subject=Free%20expert%20review&body=Hi%2C%20I%20just%20took%20the%20putter%20fitting%20quiz%20and%20I%27d%20like%20a%20deeper%20analysis.%20I%27ll%20send%20you%20a%20short%20swing%20video.',
        class: 'myvicto-quiz__dossier-secondary-cta'
      }, 'Get a free expert review →'));
      bottom.appendChild(secWrap);
    }

    dossier.appendChild(bottom);
    container.appendChild(dossier);
    root.appendChild(container);
  }

  function renderDualReco(result) {
    clearScreen();
    const ds = result.dual_screen;
    if (!ds) return renderRecoCard({ ...result, primary_reco: result.dual_left.serie, primary_url: result.dual_left.product?.url, primary_model: result.dual_left.product?.model });

    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--dual-reco' });
    container.appendChild(el('div', { class: 'myvicto-quiz__eyebrow' }, 'Honest answer'));
    /* Headline can have line breaks */
    const headlineWrap = el('h1', { class: 'myvicto-quiz__title myvicto-quiz__title--hero' });
    String(ds.headline).split('\n').forEach((line, i) => {
      if (i > 0) headlineWrap.appendChild(el('br'));
      headlineWrap.appendChild(document.createTextNode(line));
    });
    container.appendChild(headlineWrap);
    container.appendChild(el('p', { class: 'myvicto-quiz__subtitle' }, ds.subheadline));

    const cards = el('div', { class: 'myvicto-quiz__dual-cards' });

    function buildDualCard(side, sideObj) {
      const productInfo = side === 'left' ? result.dual_left.product : result.dual_right.product;
      const card = el('div', { class: 'myvicto-quiz__dual-card' });
      if (productInfo && productInfo.image_url) {
        const imgWrap = el('div', { class: 'myvicto-quiz__dual-card-image' });
        imgWrap.appendChild(el('img', { src: productInfo.image_url, alt: productInfo.model, loading: 'lazy' }));
        card.appendChild(imgWrap);
      }
      card.appendChild(el('h3', { class: 'myvicto-quiz__dual-card-title' }, sideObj.title));
      card.appendChild(el('p', { class: 'myvicto-quiz__dual-card-sub' }, sideObj.sub));
      const cta = el('a', {
        href: productInfo ? productInfo.url : '#',
        class: 'myvicto-quiz__cta myvicto-quiz__cta--primary',
        onclick: () => {
          state.recommendation = state.recommendation || {};
          state.recommendation.dual_reco_choice = sideObj.klaviyo_log;
          saveState();
          /* Fire Klaviyo update with dual choice */
          fireDualChoiceLog(sideObj.klaviyo_log);
        },
      }, sideObj.cta + ' →');
      card.appendChild(cta);
      return card;
    }

    cards.appendChild(buildDualCard('left', ds.left));
    cards.appendChild(buildDualCard('right', ds.right));
    container.appendChild(cards);

    container.appendChild(el('p', { class: 'myvicto-quiz__guarantee' }, ds.footer));
    root.appendChild(container);
  }

  function renderFittingRoute(result) {
    clearScreen();
    const fitScreen = config.fitting_screen || {};
    const container = el('div', { class: 'myvicto-quiz__screen myvicto-quiz__screen--fitting' });
    container.appendChild(el('div', { class: 'myvicto-quiz__eyebrow' }, 'Personal fitting recommended'));
    container.appendChild(el('h1', { class: 'myvicto-quiz__title myvicto-quiz__title--hero' }, fitScreen.headline || 'Your stroke deserves a real conversation.'));
    container.appendChild(el('p', { class: 'myvicto-quiz__subtitle' }, result.fitting_message || fitScreen.subheadline || 'A 15-minute video sent to our fitter beats any quiz.'));

    const subject = encodeURIComponent(result.fitting_subject || 'Fitting request');
    const mailtoLink = `mailto:${config.settings.fitting_email}?subject=${subject}`;
    container.appendChild(el('a', { href: mailtoLink, class: 'myvicto-quiz__cta myvicto-quiz__cta--primary' }, fitScreen.cta_label || 'Email our fitting expert →'));

    root.appendChild(container);
  }

  function renderFallback() {
    clearScreen();
    const container = el('div', { class: 'myvicto-quiz__screen' });
    container.appendChild(el('h1', { class: 'myvicto-quiz__title' }, 'Almost there'));
    container.appendChild(el('p', { class: 'myvicto-quiz__subtitle' }, 'We had trouble matching you. Reach out to our fitter for a personalized recommendation.'));
    container.appendChild(el('a', { href: 'mailto:' + config.settings.fitting_email, class: 'myvicto-quiz__cta myvicto-quiz__cta--primary' }, 'Email our fitter →'));
    root.appendChild(container);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // COMPLETION (fire events + render result)
  // ═══════════════════════════════════════════════════════════════════════
  function buildCompletedProperties(result) {
    const props = {
      quiz_session_id: state.sessionId,
      quiz_started_at: state.startedAt,
      quiz_completed_at: new Date().toISOString(),
      quiz_track: state.track,
      country: state.profile.country,
      language: state.profile.language,
    };
    /* Add all answers as quiz_* properties (mapped via question.klaviyo_property) */
    for (const q of config.questions) {
      const ans = state.answers[q.id];
      if (ans !== undefined && q.klaviyo_property) {
        props[q.klaviyo_property] = ans;
      }
    }
    /* Recommendation outputs */
    if (result.kind === 'single' || result.kind === 'dual') {
      props.quiz_recommended_serie = result.primary_reco || (result.dual_left && result.dual_left.serie) || null;
      props.quiz_recommended_model = result.primary_model || (result.dual_left && result.dual_left.product && result.dual_left.product.model) || null;
      props.quiz_recommended_url = result.primary_url || (result.dual_left && result.dual_left.product && result.dual_left.product.url) || null;
    }
    if (result.kind === 'dual') {
      props.quiz_dual_reco_trigger = result.dual_trigger;
    }
    if (result.kind === 'fitting') {
      props.quiz_fitting_route = result.fitting_route;
    }
    return props;
  }

  function fireQuizCompletedEvent(result) {
    const email = state.profile.email;
    if (!email) return;
    const props = buildCompletedProperties(result);

    /* Update profile properties + fire Quiz Completed event in parallel */
    Promise.allSettled([
      withRetry(() => klaviyoUpdateProfile({
        email,
        firstName: state.profile.firstName,
        customProperties: props,
      }), null),
      withRetry(() => klaviyoTrack(
        'Quiz Completed',
        { email },
        props,
        state.sessionId + '-completed'
      ), null),
    ]).then(results => {
      const ok = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
      log('info', `Klaviyo Quiz Completed: ${ok}/2 calls ok`);
    });

    /* Meta Pixel Lead event with same session_id for CAPI dedup */
    if (typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'Lead', {
          content_name: 'Myvicto Quiz Completed',
          content_category: 'putter_fitting',
          recommended_serie: props.quiz_recommended_serie || 'fitting',
        }, { eventID: state.sessionId });
        log('info', 'Meta Pixel Lead fired with eventID for CAPI dedup');
      } catch (e) {
        log('warn', 'Meta Pixel Lead failed', e.message);
      }
    } else {
      log('info', 'Meta Pixel not available (fbq undefined)');
    }
  }

  function fireDualChoiceLog(choice) {
    const email = state.profile.email;
    if (!email) return;
    /* Update profile property quiz_dual_reco_choice */
    Promise.allSettled([
      withRetry(() => klaviyoUpdateProfile({
        email,
        firstName: state.profile.firstName,
        customProperties: { quiz_dual_reco_choice: choice },
      }), null),
    ]).then(() => log('info', 'Klaviyo updated with dual reco choice', choice));
  }

  function complete() {
    state.completed = true;
    saveState();

    let result;
    try {
      result = score(state);
      log('info', 'Scoring result', result);
    } catch (e) {
      log('error', 'Scoring engine error', e);
      return renderFallback();
    }

    /* Save recommendation in state */
    state.recommendation = result;
    saveState();

    /* Fire Klaviyo + Meta events in background */
    fireQuizCompletedEvent(result);

    /* Render appropriate screen */
    if (result.kind === 'fitting') {
      renderFittingRoute(result);
    } else if (result.kind === 'dual') {
      renderDualReco(result);
    } else {
      renderRecoCard(result);
    }

    /* Scroll to top of result */
    setTimeout(() => {
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      catch (e) { window.scrollTo(0, 0); }
    }, 100);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════════════
  function init(rootElement, parsedConfig) {
    root = rootElement;
    config = parsedConfig;

    // Load existing state OR fresh
    const saved = loadState();
    state = saved || freshState();

    // Mount classes
    root.classList.add('myvicto-quiz');
    if (root.dataset.mode) root.classList.add(`myvicto-quiz--mode-${root.dataset.mode}`);

    // Drain any retry queue from previous sessions
    drainRetryQueue().catch(e => log('warn', 'Retry drain failed', e.message));

    // Resume from saved state OR start fresh
    if (saved && saved.profile.email && saved.currentScreenId !== 'email_gate') {
      // User previously completed email gate — resume
      log('info', 'Resuming saved session', { screen: saved.currentScreenId, answers: Object.keys(saved.answers).length });
      if (saved.currentScreenId === 'track_choice') {
        // V9: skip track_choice (deprecated), go to first question
        state.track = 'all';
        renderQuestion(0);
      } else if (saved.currentScreenId.startsWith('q')) {
        const questions = getActiveQuestions();
        const idx = questions.findIndex(q => q.id === saved.currentScreenId);
        renderQuestion(idx >= 0 ? idx : 0);
      } else {
        renderEmailGate();
      }
    } else {
      renderEmailGate();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // PUBLIC API + AUTO-INIT
  // ═══════════════════════════════════════════════════════════════════════
  window.MyvictoQuiz = {
    version: VERSION,
    init,
    getState: () => state ? JSON.parse(JSON.stringify(state)) : null,
    reset: () => { clearState(); state = freshState(); renderEmailGate(); },
  };

  document.addEventListener('DOMContentLoaded', () => {
    const rootEl = document.getElementById('myvicto-quiz-root');
    if (!rootEl) return;

    /* V9: ALWAYS fetch config from asset URL to bypass page HTML cache.
       Falls back to inline config if fetch fails. */
    function readInlineConfig() {
      const configEl = document.getElementById('myvicto-quiz-config-data');
      if (!configEl) return null;
      try {
        return JSON.parse(configEl.textContent);
      } catch (e) {
        log('error', 'Failed to parse inline quiz config', e);
        return null;
      }
    }

    function bootstrapConfig() {
      const fetchUrl = window.MyvictoQuizConfigUrl;
      if (!fetchUrl) {
        const inline = readInlineConfig();
        if (inline) init(rootEl, inline);
        else log('error', 'No config available (inline missing + no asset URL)');
        return;
      }
      /* Append timestamp to bypass any intermediate cache */
      const url = fetchUrl + (fetchUrl.includes('?') ? '&' : '?') + '_=' + Date.now();
      fetch(url, { cache: 'no-store' })
        .then(r => r.ok ? r.json() : Promise.reject('http ' + r.status))
        .then(cfg => {
          log('info', 'Config loaded from asset URL', { version: cfg.version });
          init(rootEl, cfg);
        })
        .catch(err => {
          log('warn', 'Asset config fetch failed, falling back to inline', err);
          const inline = readInlineConfig();
          if (inline) init(rootEl, inline);
        });
    }

    bootstrapConfig();
  });
})();
